import { useEffect, useState } from 'react';
import { Modal } from '@components/ui';
import { DocumentTextIcon } from '@heroicons/react/24/outline';
import Mustache from 'mustache';
import type { CafeTemplate } from '../types/template.types';

interface CafeTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  template: CafeTemplate | null;
  colors: {
    background: string;
    primary: string;
    text: string;
  };
  logo: string | null;
  signature?: string | null;
  extraHtml?: string | null;
  footer?: string | null;
}

export function CafeTemplateModal({
  isOpen,
  onClose,
  template,
  colors,
  logo,
  signature,
  extraHtml,
  footer,
}: CafeTemplateModalProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [renderedHtml, setRenderedHtml] = useState<string>('');

  useEffect(() => {
    if (isOpen && template) {
      loadTemplate();
    }
  }, [isOpen, template]);

  // Helper function to sanitize HTML by removing scripts
  const sanitizeHtml = (html: string): string => {
    // Remove all script tags and their content
    let sanitized = html.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
    // Remove inline event handlers (onclick, onload, etc.)
    sanitized = sanitized.replace(/\s*on\w+\s*=\s*["'][^"']*["']/gi, '');
    sanitized = sanitized.replace(/\s*on\w+\s*=\s*[^\s>]*/gi, '');
    return sanitized;
  };

  // Helper function to convert camelCase to snake_case recursively
  const normalizeToSnakeCase = (obj: any): any => {
    if (obj === null || obj === undefined) {
      return obj;
    }

    if (Array.isArray(obj)) {
      return obj.map(item => normalizeToSnakeCase(item));
    }

    if (typeof obj === 'object') {
      const normalized: any = {};

      for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
          // Convert camelCase to snake_case
          // Examples: dGen -> d_gen, gEmis -> g_emis, iTpEmis -> i_tp_emis
          let snakeKey = key.replace(/([a-z])([A-Z])/g, '$1_$2').toLowerCase();

          normalized[snakeKey] = normalizeToSnakeCase(obj[key]);
        }
      }

      return normalized;
    }

    return obj;
  };

  // Helper function to transform template HTML from snake_case to PascalCase
  // This replicates the C# backend logic: SnakeCaseToPascalCase()
  // Converts: {{d_fecha_em}} -> {{dFechaEm}}, {{g_tot_sub}} -> {{gTotSub}}
  const snakeCaseToPascalCaseTemplate = (templateHtml: string): string => {
    // Find all patterns like "_[letter]" and convert to uppercase
    // Regex matches underscore followed by any letter
    return templateHtml.replace(/_([a-zA-Z])/g, (_match, letter) => {
      return letter.toUpperCase();
    });
  };

  const convertJinja2ToMustache = (templateHtml: string, _data: any): string => {
    let result = templateHtml;

    // Replace CSS variables with actual values from the template configuration
    const cssVarMap: Record<string, string> = {
      BackgroundHeaderColor: colors.primary,
      BackgroundBodyColor: colors.background,
      BackgroundBodyDecorationColor: colors.primary,
      BackgroundFooterColor: colors.background,
      TextPrimaryColor: colors.text,
      TextSegundaryColor: colors.text,
      ButtonRedirectWebSiteColor: colors.background,
      TextButtonRedirectWebSiteColor: colors.primary,
      ButtonRedirectEivoiceColor: colors.primary,
      TextButtonRedirectEivoiceColor: colors.background,
    };

    // Replace var(--VariableName) with actual values
    Object.keys(cssVarMap).forEach((varName) => {
      const regex = new RegExp(`var\\(--${varName}\\s*\\)`, 'g');
      result = result.replace(regex, cssVarMap[varName]);
    });

    console.log('Converting Jinja2 to Mustache...');

    // Count how many for loops and endfor tags we have
    const forMatches = result.match(/\{% for (\w+) in ([\w.]+) %\}/g) || [];
    const endforMatches = result.match(/\{% endfor %\}/g) || [];
    console.log(`Found ${forMatches.length} for loops and ${endforMatches.length} endfor tags`);

    if (forMatches.length !== endforMatches.length) {
      console.error('Mismatch between for loops and endfor tags!');
    }

    // Convert for loops to Mustache sections using a stack-based approach
    // This correctly handles nested loops

    // Build a list of all positions (both for and endfor)
    interface LoopPosition {
      type: 'for' | 'endfor';
      pos: number;
      endPos: number;
      arrayName?: string;
    }

    const positions: LoopPosition[] = [];

    // Find all for loops
    const forRegex = /\{% for (\w+) in ([\w.]+) %\}/g;
    let match;
    while ((match = forRegex.exec(result)) !== null) {
      positions.push({
        type: 'for',
        pos: match.index,
        endPos: match.index + match[0].length,
        arrayName: match[2]
      });
    }

    // Find all endfor tags
    const endforRegex = /\{% endfor %\}/g;
    while ((match = endforRegex.exec(result)) !== null) {
      positions.push({
        type: 'endfor',
        pos: match.index,
        endPos: match.index + match[0].length
      });
    }

    // Sort by position
    positions.sort((a, b) => a.pos - b.pos);

    console.log(`Found ${positions.filter(p => p.type === 'for').length} for loops:`,
      positions.filter(p => p.type === 'for').map(p => p.arrayName));

    // Use a stack to match for loops with their corresponding endfor
    // We need to track both the array name and the loop variable name
    interface LoopContext {
      arrayName: string;
      varName: string;
      startPos: number;
      endPos: number;
    }

    const stack: LoopContext[] = [];
    const replacements: Array<{ pos: number; endPos: number; replacement: string }> = [];
    const loopContexts: LoopContext[] = [];

    // First pass: identify all loop contexts
    const forRegex2 = /\{% for (\w+) in ([\w.]+) %\}/g;
    let forMatch;
    const forPositions: Array<{ pos: number; endPos: number; varName: string; arrayName: string }> = [];

    while ((forMatch = forRegex2.exec(result)) !== null) {
      forPositions.push({
        pos: forMatch.index,
        endPos: forMatch.index + forMatch[0].length,
        varName: forMatch[1],    // e.g., "Item"
        arrayName: forMatch[2]    // e.g., "FElectronica.gItem"
      });
    }

    positions.forEach((position) => {
      if (position.type === 'for' && position.arrayName) {
        // Find the matching for position to get the variable name
        const forPos = forPositions.find(fp => fp.pos === position.pos);
        if (forPos) {
          const context: LoopContext = {
            arrayName: position.arrayName,
            varName: forPos.varName,
            startPos: position.endPos,
            endPos: -1 // Will be set when we find the matching endfor
          };
          stack.push(context);

          replacements.push({
            pos: position.pos,
            endPos: position.endPos,
            replacement: `{{#${position.arrayName}}}`
          });
        }
      } else if (position.type === 'endfor') {
        // Pop from stack and add closing tag replacement
        const context = stack.pop();
        if (context) {
          context.endPos = position.pos;
          loopContexts.push(context);

          replacements.push({
            pos: position.pos,
            endPos: position.endPos,
            replacement: `{{/${context.arrayName}}}`
          });
        }
      }
    });

    // Apply replacements from end to start to preserve positions
    replacements.sort((a, b) => b.pos - a.pos);
    replacements.forEach((repl) => {
      const before = result.substring(0, repl.pos);
      const after = result.substring(repl.endPos);
      result = before + repl.replacement + after;
    });

    // 🔧 CRITICAL FIX: Remove loop variable prefixes from Mustache templates
    // In Jinja2: {% for Item in array %} {{Item.property}} {% endfor %}
    // In Mustache: {{#array}} {{property}} {{/array}}  ← No "Item." prefix needed!
    console.log('Removing loop variable prefixes for Mustache compatibility...');
    loopContexts.forEach(context => {
      // Replace {{VarName.property}} with {{property}} within this loop's context
      // But only for the immediate properties, not nested paths
      const varPrefixRegex = new RegExp(`\\{\\{${context.varName}\\.([\\w.]+)\\}\\}`, 'g');

      result = result.replace(varPrefixRegex, (match, propertyPath) => {
        console.log(`  Replacing ${match} with {{${propertyPath}}}`);
        return `{{${propertyPath}}}`;
      });
    });

    console.log('Conversion complete');

    // Remove else blocks first (before removing if tags)
    result = result.replace(/\{% else %\}[\s\S]*?\{% endif %\}/g, '{% endif %}');

    // Remove if/endif/elsif/else tags - IMPROVED REGEX
    // Match {% if ... %} with any content inside including dots, operators, etc.
    result = result.replace(/\{%\s*if\s+[^%]+\s*%\}/g, '');
    result = result.replace(/\{%\s*elsif\s+[^%]+\s*%\}/g, '');
    result = result.replace(/\{%\s*endif\s*%\}/g, '');
    result = result.replace(/\{%\s*else\s*%\}/g, '');

    // Remove handlebars conditionals
    result = result.replace(/\{\{#if\s+([^}]+)\}\}/g, '');
    result = result.replace(/\{\{\/if\}\}/g, '');

    // Verify the conversion
    const mustacheOpens = result.match(/\{\{#([\w.]+)\}\}/g) || [];
    const mustacheCloses = result.match(/\{\{\/([\w.]+)\}\}/g) || [];
    console.log('Mustache opens:', mustacheOpens);
    console.log('Mustache closes:', mustacheCloses);

    // Check for remaining Jinja2 tags
    const remainingJinja = result.match(/\{%[^%]+%\}/g);
    if (remainingJinja && remainingJinja.length > 0) {
      console.warn('Warning: Remaining Jinja2 tags found:', remainingJinja.slice(0, 5));
    }

    return result;
  };

  const loadTemplate = async () => {
    if (!template) return;

    try {
      setIsLoading(true);
      setError(null);

      console.log('Loading CAFE template:', template.displayName);

      // Parse the JSON test model
      let testData = JSON.parse(template.jsonTestModel);
      console.log('CAFE test data parsed:', Object.keys(testData));

      // Check if FElectronica is a string that needs to be parsed
      if (testData.FElectronica && typeof testData.FElectronica === 'string') {
        console.log('FElectronica is a string, parsing it...');
        try {
          testData.FElectronica = JSON.parse(testData.FElectronica);
          console.log('FElectronica parsed successfully');
        } catch (err) {
          console.error('Error parsing FElectronica:', err);
        }
      }

      // 🎯 CRITICAL: Check data format from endpoint
      // Based on AludraPortalEInvoice analysis:
      // - jsonTestModel can come in camelCase (dGen, gEmis) OR snake_case (d_gen, g_emis)
      // - The template HTML ALWAYS comes in snake_case ({{d_gen}}, {{g_emis}})
      // - Backend C# transforms template HTML: snake_case → PascalCase
      // - Then renders with DotLiquid using CSharpNamingConvention

      if (testData.FElectronica && typeof testData.FElectronica === 'object') {
        const hasCamelCase = 'dGen' in testData.FElectronica;
        const hasSnakeCase = 'd_gen' in testData.FElectronica;

        console.log('📊 Data format detection:', {
          hasCamelCase,
          hasSnakeCase,
          keys: Object.keys(testData.FElectronica).slice(0, 5)
        });

        // If data comes in camelCase, keep it as-is
        // If data comes in snake_case, we might need to transform it to camelCase
        // to match the transformed template

        if (hasSnakeCase && !hasCamelCase) {
          console.log('⚠️ Data is in snake_case, but template will be transformed to camelCase');
          console.log('💡 We should keep data as-is and let the template transformation handle it');
        }
      }

      // Check if Impuestos is a string that needs to be parsed
      if (testData.Impuestos && typeof testData.Impuestos === 'string') {
        console.log('Impuestos is a string, parsing it...');
        try {
          testData.Impuestos = JSON.parse(testData.Impuestos);
          console.log('Impuestos parsed successfully');
        } catch (err) {
          console.error('Error parsing Impuestos:', err);
        }
      }

      console.log('Sample of test data:', {
        DocumentTitle: testData.DocumentTitle,
        CompanyName: testData.CompanyName,
        Number: testData.Number,
        Items: testData.Items?.length,
        FElectronica: testData.FElectronica ? 'exists' : 'missing'
      });

      // Check if FElectronica has the necessary nested structure (check both formats)
      const dGen = testData.FElectronica?.dGen || testData.FElectronica?.d_gen;
      const gItem = testData.FElectronica?.gItem || testData.FElectronica?.g_item;
      const gTot = testData.FElectronica?.gTot || testData.FElectronica?.g_tot;

      const hasValidStructure = testData.FElectronica
        && dGen
        && (dGen.gEmis || dGen.g_emis)
        && (dGen.gDatRec || dGen.g_dat_rec)
        && gItem
        && Array.isArray(gItem)
        && gItem.length > 0
        && gTot;

      // Log the actual structure we have
      console.log('Structure check:', {
        hasFElectronica: !!testData.FElectronica,
        hasDGen: !!dGen,
        hasGEmis: !!(dGen?.gEmis || dGen?.g_emis),
        hasGDatRec: !!(dGen?.gDatRec || dGen?.g_dat_rec),
        hasGItem: !!gItem,
        gItemLength: gItem?.length,
        hasGTot: !!gTot,
        hasValidStructure,
        dataFormat: testData.FElectronica?.dGen ? 'camelCase' : 'snake_case'
      });

      // Log sample data from g_item and g_tot (check both formats)
      if (gItem && gItem.length > 0) {
        const firstItem = gItem[0];
        console.log('Sample gItem[0]:', {
          dSecItem: firstItem.dSecItem || firstItem.d_sec_item,
          dCodProd: firstItem.dCodProd || firstItem.d_cod_prod,
          dDescProd: firstItem.dDescProd || firstItem.d_desc_prod,
          gPrecios: firstItem.gPrecios || firstItem.g_precios
        });
      }

      if (gTot) {
        console.log('Sample gTot:', {
          dTotNeto: gTot.dTotNeto || gTot.d_tot_neto,
          dTotItbms: gTot.dTotItbms || gTot.d_tot_itbms,
          dTotGravado: gTot.dTotGravado || gTot.d_tot_gravado,
          dVTot: gTot.dVTot || gTot.d_v_tot,
          dTotRec: gTot.dTotRec || gTot.d_tot_rec
        });
      }

      // 📊 DATA INTEGRITY CHECK
      // Based on AludraPortalEInvoice analysis:
      // - jsonTestModel already contains ALL data needed
      // - We just need to map Logo and CompanyName (like C# does)
      // - NO data transformation needed - keep original format

      console.log('📊 Checking data integrity...');

      // Calculate ExtraDataByItems ONLY if missing (use both format accessors)
      if (!testData.ExtraDataByItems || testData.ExtraDataByItems.length === 0) {
        console.log('⚙️ Calculating ExtraDataByItems...');

        if (gItem && Array.isArray(gItem)) {
          testData.ExtraDataByItems = gItem.map((item: any) => {
            // Calculate total taxes for this item (check both formats)
            let totalTaxes = 0;

            const gItbmsItem = item.gItbmsItem || item.g_itbms_item;
            const gIscItem = item.gIscItem || item.g_isc_item;
            const gOtiItem = item.gOtiItem || item.g_oti_item;

            // Add ITBMS
            if (gItbmsItem?.dValItbms || gItbmsItem?.d_val_itbms) {
              totalTaxes += parseFloat(gItbmsItem.dValItbms || gItbmsItem.d_val_itbms || '0');
            }

            // Add ISC if exists
            if (gIscItem?.dValIsc || gIscItem?.d_val_isc) {
              totalTaxes += parseFloat(gIscItem.dValIsc || gIscItem.d_val_isc || '0');
            }

            // Add OTI if exists
            if (gOtiItem?.dValOti || gOtiItem?.d_val_oti) {
              totalTaxes += parseFloat(gOtiItem.dValOti || gOtiItem.d_val_oti || '0');
            }

            return {
              dSecItem: item.dSecItem || item.d_sec_item,
              dTotTaxes: totalTaxes.toFixed(2),
              dInfEmFe: item.dInfEmFe || item.d_inf_em_fe || null
            };
          });

          console.log('✅ ExtraDataByItems calculated:', testData.ExtraDataByItems.length, 'items');
        }
      }

      // 2. Ensure all descriptive fields are available and properly exposed
      console.log('📝 Ensuring descriptive data fields are available...');

      // Log export information if available
      if (testData.FElectronica?.d_gen?.g_f_exp) {
        console.log('💱 Export Information (gFExp):', {
          condicionEntrega: testData.FElectronica.d_gen.g_f_exp.c_cond_entr,
          moneda: testData.FElectronica.d_gen.g_f_exp.c_moneda,
          tipoCambio: testData.FElectronica.d_gen.g_f_exp.d_cambio,
          valorTotal: testData.FElectronica.d_gen.g_f_exp.d_v_tot_est,
          puertoEmbarque: testData.FElectronica.d_gen.g_f_exp.d_puerto_embarq
        });
      }

      // Log payment terms if available
      if (testData.FElectronica?.g_tot?.g_pag_plazo && Array.isArray(testData.FElectronica.g_tot.g_pag_plazo)) {
        console.log('💳 Payment Terms (gPagPlazo):', testData.FElectronica.g_tot.g_pag_plazo.map((p: any) => ({
          secuencia: p.d_sec_item,
          fecha: p.d_fec_it_plazo,
          valor: p.d_val_it_plazo,
          descripcion: p.d_inf_pag_plazo
        })));
      }

      // Log additional item information
      if (testData.FElectronica?.g_item && Array.isArray(testData.FElectronica.g_item)) {
        testData.FElectronica.g_item.forEach((item: any, index: number) => {
          if (item.d_inf_em_fe) {
            console.log(`📦 Item ${index + 1} - Additional Info:`, item.d_inf_em_fe);
          }
        });
      }

      // Log foreign ID if available
      if (testData.FElectronica?.d_gen?.g_dat_rec?.g_id_ext) {
        console.log('🌍 Foreign ID (gIdExt):', {
          id: testData.FElectronica.d_gen.g_dat_rec.g_id_ext.d_id_ext,
          pais: testData.FElectronica.d_gen.g_dat_rec.g_id_ext.d_pais_ext
        });
      }

      // 3. Detect and fix inconsistent data (test values vs real values)
      if (testData.FElectronica?.g_tot) {
        const rootSubtotal = parseFloat(testData.Subtotal || '0');
        const feTotNeto = parseFloat(testData.FElectronica.g_tot.d_tot_neto || '0');

        console.log('🔍 Comparing values:', {
          rootSubtotal,
          feTotNeto,
          ratio: rootSubtotal / feTotNeto
        });

        // If root-level Subtotal is significantly larger than FElectronica totals (>10x),
        // it means FElectronica has test data and we should calculate from available data
        if (rootSubtotal > feTotNeto * 10) {
          console.log('⚠️ Detected test data in FElectronica.g_tot, calculating correct values...');

          // Calculate totals from Impuestos array
          let totalImpuestos = 0;
          if (testData.Impuestos && Array.isArray(testData.Impuestos)) {
            totalImpuestos = testData.Impuestos.reduce((sum: number, imp: any) => {
              return sum + parseFloat(imp.MontoImpuesto || imp.monto_impuesto || '0');
            }, 0);
          }

          // Update g_tot with calculated values
          const newTotNeto = rootSubtotal.toFixed(2);
          const newTotGravado = totalImpuestos.toFixed(2);
          const newVTot = (rootSubtotal + totalImpuestos).toFixed(2);

          testData.FElectronica.g_tot.d_tot_neto = newTotNeto;
          testData.FElectronica.g_tot.d_tot_gravado = newTotGravado;
          testData.FElectronica.g_tot.d_tot_itbms = newTotGravado;
          testData.FElectronica.g_tot.d_v_tot = newVTot;
          testData.FElectronica.g_tot.d_tot_rec = newVTot;

          console.log('✅ Updated FElectronica.g_tot:', {
            d_tot_neto: newTotNeto,
            d_tot_gravado: newTotGravado,
            d_v_tot: newVTot,
            d_tot_rec: newVTot
          });

          // Also update the payment amount
          if (testData.FElectronica.g_tot.g_forma_pago &&
              Array.isArray(testData.FElectronica.g_tot.g_forma_pago) &&
              testData.FElectronica.g_tot.g_forma_pago.length > 0) {
            testData.FElectronica.g_tot.g_forma_pago[0].d_vlr_cuota = newVTot;
            console.log('✅ Updated payment amount:', newVTot);
          }
        } else {
          console.log('✅ Data looks consistent, no synchronization needed');
        }

        // Calculate d_v_tot if missing
        if (!testData.FElectronica.g_tot.d_v_tot) {
          const totNeto = parseFloat(testData.FElectronica.g_tot.d_tot_neto || '0');
          const totGravado = parseFloat(testData.FElectronica.g_tot.d_tot_gravado || '0');
          testData.FElectronica.g_tot.d_v_tot = (totNeto + totGravado).toFixed(2);
          console.log('✅ Calculated missing d_v_tot:', testData.FElectronica.g_tot.d_v_tot);
        }
      }

      console.log('📊 Final calculated values:', {
        Subtotal: testData.Subtotal,
        'FElectronica.g_tot.d_tot_neto': testData.FElectronica?.g_tot?.d_tot_neto,
        'FElectronica.g_tot.d_v_tot': testData.FElectronica?.g_tot?.d_v_tot,
        'ExtraDataByItems': testData.ExtraDataByItems?.length || 0
      });

      console.log('📋 Summary of all descriptive data available:', {
        'Export Info (gFExp)': !!testData.FElectronica?.d_gen?.g_f_exp,
        'Payment Terms (gPagPlazo)': testData.FElectronica?.g_tot?.g_pag_plazo?.length || 0,
        'Additional Item Info (dInfEmFE)': testData.FElectronica?.g_item?.filter((i: any) => i.d_inf_em_fe).length || 0,
        'Foreign ID (gIdExt)': !!testData.FElectronica?.d_gen?.g_dat_rec?.g_id_ext,
        'Product Descriptions': testData.FElectronica?.g_item?.length || 0,
        'Tax Breakdown': testData.Impuestos?.length || 0,
        'Items with pricing': testData.FElectronica?.g_item?.filter((i: any) => i.g_precios).length || 0
      });

      // Log if structure validation failed - but DON'T replace the data from endpoint
      if (!hasValidStructure) {
        console.warn('FElectronica structure validation failed, but keeping original data from endpoint');
        console.log('Current FElectronica structure:', testData.FElectronica ? Object.keys(testData.FElectronica) : 'null');
        console.log('Note: Template will render with whatever data came from endpoint');
      }

      // Only set default values for UI-related fields that don't affect data integrity
      // DO NOT modify financial data or core invoice data

      // Set default BranchOfficeName only if missing (non-financial)
      if (!testData.BranchOfficeName) {
        testData.BranchOfficeName = testData.CompanyStore || '';
      }

      // Set default SeguroAcarreoItem only if missing (needed for calculations)
      if (!testData.SeguroAcarreoItem) {
        testData.SeguroAcarreoItem = '0.00';
      }

      // Log what data we have from endpoint (DO NOT modify)
      console.log('Data from endpoint (not modified):', {
        hasImpuestos: !!testData.Impuestos,
        impuestosCount: Array.isArray(testData.Impuestos) ? testData.Impuestos.length : 0,
        hasExtraDataByItems: !!testData.ExtraDataByItems,
        extraDataCount: Array.isArray(testData.ExtraDataByItems) ? testData.ExtraDataByItems.length : 0,
        hasCUFE: !!testData.CUFE,
        hasAuthProtocol: !!testData.AuthProtocol,
        hasQrcode: !!testData.Qrcode,
        hasCreatedDate: !!testData.CreatedDate,
        hasCreatedHour: !!testData.CreatedHour,
      });

      console.log('FElectronica structure from endpoint:', {
        d_gen: !!testData.FElectronica?.d_gen,
        g_item: testData.FElectronica?.g_item?.length || 0,
        g_tot: !!testData.FElectronica?.g_tot
      });
      console.log('Using colors from template configuration:', {
        background: colors.background,
        primary: colors.primary,
        text: colors.text,
      });
      console.log('Using logo:', logo);

      // Calculate combined values that Mustache can't compute
      // Mustache doesn't support arithmetic operations, so we need to pre-calculate
      const seguroAcarreoValue = parseFloat(testData.SeguroAcarreoItem || '0');
      const totNetoValue = parseFloat(testData.FElectronica?.g_tot?.d_tot_neto || '0');
      const totNetoWithSeguroAcarreo = (totNetoValue + seguroAcarreoValue).toFixed(2);

      // Generate descriptive labels and enrich data for better display
      const descriptiveLabels: any = {};

      // Add descriptive labels for export info
      if (testData.FElectronica?.d_gen?.g_f_exp) {
        const fexp = testData.FElectronica.d_gen.g_f_exp;
        descriptiveLabels.ExportInfoLabel = `Exportación - ${fexp.c_moneda || 'N/A'} (TC: ${fexp.d_cambio || 'N/A'})`;
        descriptiveLabels.ExportConditionsLabel = `Condiciones de entrega: ${fexp.c_cond_entr || 'N/A'}`;
        descriptiveLabels.ExportPortLabel = `Puerto de embarque: ${fexp.d_puerto_embarq || 'N/A'}`;
        descriptiveLabels.ExportInfoCompact = `${fexp.c_moneda || 'N/A'} (TC: ${fexp.d_cambio || 'N/A'}) - ${fexp.c_cond_entr || 'N/A'}`;
      }

      // Add descriptive labels for payment terms
      if (testData.FElectronica?.g_tot?.g_pag_plazo && Array.isArray(testData.FElectronica.g_tot.g_pag_plazo)) {
        descriptiveLabels.HasPaymentTerms = true;
        descriptiveLabels.PaymentTermsCount = testData.FElectronica.g_tot.g_pag_plazo.length;

        // Format payment terms for display
        testData.FElectronica.g_tot.g_pag_plazo_formatted = testData.FElectronica.g_tot.g_pag_plazo.map((pago: any) => {
          const fecha = pago.d_fec_it_plazo ? new Date(pago.d_fec_it_plazo).toLocaleDateString('es-PA') : 'N/A';
          return {
            ...pago,
            fecha_formatted: fecha,
            descripcion_completa: `${pago.d_inf_pag_plazo || ''} (Vence: ${fecha})`
          };
        });
      }

      // Add descriptive labels for totals
      if (testData.FElectronica?.g_tot) {
        const gtot = testData.FElectronica.g_tot;
        descriptiveLabels.SubtotalLabel = `Subtotal (antes de impuestos)`;
        descriptiveLabels.TotalTaxLabel = `Total Impuestos`;
        descriptiveLabels.GrandTotalLabel = `Total a Pagar`;

        // Add tax rate description if ITBMS is available
        if (gtot.d_tot_itbms && parseFloat(gtot.d_tot_itbms) > 0) {
          const itbmsPercent = ((parseFloat(gtot.d_tot_itbms) / parseFloat(gtot.d_tot_neto || '1')) * 100).toFixed(1);
          descriptiveLabels.ITBMSLabel = `ITBMS (${itbmsPercent}%)`;
          descriptiveLabels.ITBMSPercent = itbmsPercent;
        }
      }

      // Add descriptive labels for items
      if (testData.FElectronica?.g_item && Array.isArray(testData.FElectronica.g_item)) {
        descriptiveLabels.ItemsCount = testData.FElectronica.g_item.length;
        descriptiveLabels.ItemsLabel = `${testData.FElectronica.g_item.length} producto${testData.FElectronica.g_item.length > 1 ? 's' : ''}`;

        // Add formatted descriptions to items
        testData.FElectronica.g_item = testData.FElectronica.g_item.map((item: any) => ({
          ...item,
          d_desc_prod_full: item.d_inf_em_fe
            ? `${item.d_desc_prod}\n${item.d_inf_em_fe}`
            : item.d_desc_prod,
          has_additional_info: !!item.d_inf_em_fe
        }));
      }

      // Add foreign ID formatted
      if (testData.FElectronica?.d_gen?.g_dat_rec?.g_id_ext) {
        const gIdExt = testData.FElectronica.d_gen.g_dat_rec.g_id_ext;
        testData.FElectronica.d_gen.g_dat_rec.foreign_id_formatted = `ID: ${gIdExt.d_id_ext} (${gIdExt.d_pais_ext})`;
      }

      // 🔧 CRITICAL: Create a dual-case proxy for FElectronica
      // Mustache doesn't have C#'s CSharpNamingConvention automatic property name conversion
      // So we need to ensure the data object has BOTH snake_case and camelCase properties
      const createDualCaseProxy = (obj: any): any => {
        if (!obj || typeof obj !== 'object') return obj;

        if (Array.isArray(obj)) {
          return obj.map(item => createDualCaseProxy(item));
        }

        const result: any = {};

        Object.keys(obj).forEach(key => {
          const value = obj[key];

          // Add original key with recursively transformed value
          result[key] = typeof value === 'object' && value !== null ? createDualCaseProxy(value) : value;

          // Add camelCase version if key is snake_case
          if (key.includes('_')) {
            const camelKey = key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
            if (camelKey !== key) {
              result[camelKey] = result[key];
            }
          }

          // Add snake_case version if key is camelCase
          if (/[a-z][A-Z]/.test(key)) {
            const snakeKey = key.replace(/([a-z])([A-Z])/g, '$1_$2').toLowerCase();
            if (snakeKey !== key) {
              result[snakeKey] = result[key];
            }
          }
        });

        return result;
      };

      // Transform FElectronica to support both naming conventions
      if (testData.FElectronica) {
        console.log('🔄 Creating dual-case proxy for FElectronica (to support both snake_case and camelCase)...');
        testData.FElectronica = createDualCaseProxy(testData.FElectronica);
        console.log('✅ FElectronica transformed to support both naming conventions');
      }

      // Add logo and colors to test data if available
      // Solo usar el logo configurado en Templates, sin fallbacks
      const logoUrl = logo || '';
      const templateData = {
        ...testData,
        Logo: logoUrl,
        logo: logoUrl, // Some templates use lowercase 'logo'
        ColorPrimary: colors.primary || '#12C4B2',
        ColorHeader: colors.primary || '#12C4B2',
        ColorFooter: colors.background || '#FFFFFF',
        // Add pre-calculated values for Mustache
        TotNetoWithSeguroAcarreo: totNetoWithSeguroAcarreo,
        // Add descriptive labels
        ...descriptiveLabels,
        // Add both CUFE and Cufe for compatibility
        CUFE: testData.CUFE || testData.Cufe || testData.cufe,
        Cufe: testData.Cufe || testData.CUFE || testData.cufe,
      };

      // Log descriptive labels added
      console.log('🏷️ Descriptive labels generated:', descriptiveLabels);

      // Log some variables being used in the template
      const variablesInTemplate = template.templateHtml.match(/\{\{([^}#/]+)\}\}/g) || [];
      console.log('Variables found in template (first 20):', variablesInTemplate.slice(0, 20));

      // 🎯 REPLICATE C# BACKEND LOGIC EXACTLY
      // C# does: Template.Parse(SnakeCaseToPascalCase(htmTemplate))
      // This transforms the TEMPLATE HTML from snake_case to camelCase
      // The DATA stays in its original format (camelCase)

      console.log('🔄 Transforming TEMPLATE HTML from snake_case to camelCase (like C# backend)...');

      // Step 1: Transform template HTML: {{d_gen}} → {{dGen}}
      const transformedTemplateHtml = snakeCaseToPascalCaseTemplate(template.templateHtml);
      console.log('✅ Template HTML transformed from snake_case to camelCase');

      // Log transformed variables to verify transformation
      const transformedVars = transformedTemplateHtml.match(/\{\{([^}#/]+)\}\}/g) || [];
      console.log('🔍 Variables after transformation (first 20):', transformedVars.slice(0, 20));

      // Step 1.5: Replace the complex if/elsif logic for Forma de Pago with simple {{paymentMethodText}}
      // This is a pre-processing step before converting to Mustache
      console.log('🔄 Replacing Forma de Pago if/elsif logic with simple {{paymentMethodText}}...');

      // Pattern to match the entire if/elsif/endif block for payment methods
      // This pattern looks for: {% if Pago.i_forma_pago == "01" %} ... {% endif %}
      // Including all the {% elsif %} in between
      // Support both snake_case (i_forma_pago) and camelCase (iFormaPago)
      const formaPagoPattern = /\{%\s*if\s+Pago\.(i_forma_pago|iFormaPago)\s*==\s*"01"\s*%\}[\s\S]*?(?:\{%\s*elsif\s+Pago\.(i_forma_pago|iFormaPago)\s*==\s*"[^"]+"\s*%\}[\s\S]*?)*\{%\s*endif\s*%\}/g;

      let transformedWithFormaPago = transformedTemplateHtml;
      const matches = transformedTemplateHtml.match(formaPagoPattern);

      if (matches) {
        console.log(`  Found ${matches.length} Forma de Pago conditional block(s)`);
        transformedWithFormaPago = transformedTemplateHtml.replace(formaPagoPattern, (match) => {
          console.log('  Replacing complex if/elsif block with {{paymentMethodText}}');
          console.log('  Original block length:', match.length, 'chars');
          // Replace the entire if/elsif block with just the paymentMethodText
          return '<td>{{paymentMethodText}}</td>';
        });
        console.log('✅ Forma de Pago logic simplified');
      } else {
        console.log('  No Forma de Pago conditional block found - this is OK if template uses different format');
        transformedWithFormaPago = transformedTemplateHtml;
      }

      // Step 2: Convert Jinja2/DotLiquid syntax to Mustache (with transformed variable names)
      let mustacheTemplate = convertJinja2ToMustache(transformedWithFormaPago, templateData);

      // Replace arithmetic expressions that Mustache can't handle
      // Example: {{FElectronica.g_tot.d_tot_neto + SeguroAcarreoItem}} -> {{TotNetoWithSeguroAcarreo}}
      mustacheTemplate = mustacheTemplate.replace(
        /\{\{FElectronica\.g_tot\.d_tot_neto \+ SeguroAcarreoItem\}\}/g,
        '{{TotNetoWithSeguroAcarreo}}'
      );

      console.log('CAFE template converted, length:', mustacheTemplate.length);

      // Debug: Check for unclosed sections
      const openSections = mustacheTemplate.match(/\{\{#[\w.]+\}\}/g) || [];
      const closeSections = mustacheTemplate.match(/\{\{\/[\w.]+\}\}/g) || [];
      console.log('Open sections found:', openSections.length, openSections);
      console.log('Close sections found:', closeSections.length, closeSections);

      // Debug: Log a sample of the template data being passed to Mustache
      console.log('🎯 Template data sample being passed to Mustache:', {
        Logo: templateData.Logo,
        DocumentTitle: templateData.DocumentTitle,
        CompanyName: templateData.CompanyName,
        CUFE: templateData.CUFE,
        Cufe: templateData.Cufe,
        CreatedDate: templateData.CreatedDate,
        CreatedHour: templateData.CreatedHour,
        Email: templateData.Email,
        BranchOfficeName: templateData.BranchOfficeName,
        Qrcode: templateData.Qrcode,
        'FElectronica (type)': typeof templateData.FElectronica,
        'FElectronica.dGen': templateData.FElectronica?.dGen ? 'exists' : 'missing',
        'FElectronica.gItem': templateData.FElectronica?.gItem ? `${templateData.FElectronica.gItem.length} items` : 'missing',
        'FElectronica.gTot': templateData.FElectronica?.gTot ? 'exists' : 'missing',
        'Impuestos': templateData.Impuestos ? `${templateData.Impuestos.length} impuestos` : 'missing',
      });

      // Log detailed FElectronica structure
      if (templateData.FElectronica) {
        console.log('🔍 FElectronica structure:', {
          keys: Object.keys(templateData.FElectronica).slice(0, 10),
          dGen: templateData.FElectronica.dGen ? {
            keys: Object.keys(templateData.FElectronica.dGen).slice(0, 10),
            hasGEmis: !!templateData.FElectronica.dGen.gEmis,
            hasGDatRec: !!templateData.FElectronica.dGen.gDatRec,
          } : 'missing',
          gItem: templateData.FElectronica.gItem ? {
            count: templateData.FElectronica.gItem.length,
            firstItem: templateData.FElectronica.gItem[0] ? Object.keys(templateData.FElectronica.gItem[0]).slice(0, 10) : 'no items'
          } : 'missing',
          gTot: templateData.FElectronica.gTot ? {
            type: Array.isArray(templateData.FElectronica.gTot) ? 'ARRAY (ERROR!)' : 'object',
            keys: Array.isArray(templateData.FElectronica.gTot) ? 'N/A' : Object.keys(templateData.FElectronica.gTot).slice(0, 10),
            value: templateData.FElectronica.gTot
          } : 'missing',
        });

        // ⚠️ CRITICAL BUG FIX: gTot is coming as an array when it should be an object
        if (Array.isArray(templateData.FElectronica.gTot)) {
          console.error('🚨 CRITICAL: gTot is an array, should be an object! Taking first element...');
          templateData.FElectronica.gTot = templateData.FElectronica.gTot[0] || {};
          console.log('✅ Fixed: gTot is now:', templateData.FElectronica.gTot);
        }

        // 🔧 FIX: Pre-process gFormaPago to add descriptive text for each payment method
        // This allows Mustache to display the text without needing if/elsif conditionals
        const paymentMethodMap: Record<string, string> = {
          '01': 'Crédito',
          '02': 'Efectivo',
          '03': 'Tarjeta de Crédito',
          '04': 'Tarjeta de Débito',
          '05': 'Tarjeta de Fidelización',
          '06': 'Vale',
          '07': 'Tarjeta de Regalo',
          '08': 'Transf./Dep. a cta. Bancaria',
          '09': 'Cheque',
          '10': 'Punto de Pago',
          '99': 'Otro'
        };

        // Process both camelCase and snake_case versions
        if (templateData.FElectronica.gTot?.gFormaPago) {
          templateData.FElectronica.gTot.gFormaPago = templateData.FElectronica.gTot.gFormaPago.map((pago: any) => ({
            ...pago,
            paymentMethodText: paymentMethodMap[pago.iFormaPago || pago.i_forma_pago] || 'Desconocido'
          }));
          console.log('✅ Added paymentMethodText to gFormaPago:', templateData.FElectronica.gTot.gFormaPago);
        }

        if (templateData.FElectronica.gTot?.g_forma_pago) {
          templateData.FElectronica.gTot.g_forma_pago = templateData.FElectronica.gTot.g_forma_pago.map((pago: any) => ({
            ...pago,
            paymentMethodText: paymentMethodMap[pago.iFormaPago || pago.i_forma_pago] || 'Desconocido'
          }));
          console.log('✅ Added paymentMethodText to g_forma_pago:', templateData.FElectronica.gTot.g_forma_pago);
        }
      }

      // Log g_tot details if it exists
      if (templateData.FElectronica?.g_tot) {
        console.log('FElectronica.g_tot details:', {
          d_tot_neto: templateData.FElectronica.g_tot.d_tot_neto,
          d_tot_gravado: templateData.FElectronica.g_tot.d_tot_gravado,
          d_tot_desc: templateData.FElectronica.g_tot.d_tot_desc,
          d_v_tot: templateData.FElectronica.g_tot.d_v_tot,
          d_tot_rec: templateData.FElectronica.g_tot.d_tot_rec,
          d_vuelto: templateData.FElectronica.g_tot.d_vuelto,
          g_forma_pago: templateData.FElectronica.g_tot.g_forma_pago ? `${templateData.FElectronica.g_tot.g_forma_pago.length} formas` : 'missing',
        });
      }

      // Log item details
      if (templateData.FElectronica?.g_item && templateData.FElectronica.g_item.length > 0) {
        console.log('First item details:', {
          d_cod_prod: templateData.FElectronica.g_item[0].d_cod_prod,
          d_desc_prod: templateData.FElectronica.g_item[0].d_desc_prod,
          g_precios: templateData.FElectronica.g_item[0].g_precios,
        });
      }

      // Render the template with Mustache
      let html = Mustache.render(mustacheTemplate, templateData);
      console.log('CAFE template rendered, length:', html.length);

      // Check if the rendered HTML contains actual data or just placeholders
      const hasData = html.includes(templateData.CUFE || 'CUFE_TEST');
      console.log('Rendered HTML contains CUFE?', hasData);

      // Check for unreplaced variables (debugging)
      const unreplacedVars = html.match(/\{\{([^}]+)\}\}/g) || [];
      if (unreplacedVars.length > 0) {
        console.warn('⚠️ Found', unreplacedVars.length, 'unreplaced variables in HTML:');
        console.warn('First 20 unreplaced variables:', unreplacedVars.slice(0, 20));

        // Show unique variable names (remove duplicates)
        const uniqueVars = Array.from(new Set(unreplacedVars));
        console.warn(`Total unique unreplaced variables: ${uniqueVars.length}/${unreplacedVars.length}`);
        console.warn('Unique variables (first 20):', uniqueVars.slice(0, 20));
      } else {
        console.log('✅ All variables were replaced successfully!');
      }

      // ✅ TEMPLATE IS NOW READY
      // - All data is already in testData (from jsonTestModel)
      // - Template has been transformed from snake_case to camelCase
      // - Just needs to be rendered with Mustache
      console.log('✅ Template ready for rendering');

      // 🎯 REPLICAR EXACTAMENTE LA LÓGICA DEL PROYECTO C# (AludraPortalEInvoice)
      // En C#: LoadCafeTemplate.cshtml usa un contenedor con padding:20px y background blanco
      // El iframe es de 1200px de alto y 100% de ancho
      // Solo se inyecta el HTML renderizado con @Html.Raw(ViewBag.Html)

      // Inyectar firma, extraHtml y footer en el HTML renderizado
      let finalHtml = html;

      // 1. Inyectar firma al final del comprobante (antes del footer)
      if (signature) {
        const signatureHtml = `
          <div style="margin-top: 60px; margin-bottom: 20px; text-align: right; padding-right: 60px; page-break-inside: avoid;">
            <div style="display: inline-block; text-align: center;">
              <img src="${signature}" alt="Firma" style="max-width: 300px; max-height: 150px; height: auto; display: block; margin: 0 auto;" />
              <div style="margin-top: 8px; border-top: 2px solid #333; padding-top: 4px; font-size: 12px; color: #555;">
                Firma Autorizada
              </div>
            </div>
          </div>
        `;
        finalHtml = finalHtml + signatureHtml;
      }

      // 2. Inyectar página extra (términos y condiciones) después de la firma
      // Verificar que extraHtml tenga contenido real (no solo tags vacíos de ReactQuill)
      const extraTextOnly = (extraHtml || '')
        .replace(/<br\s*\/?>/gi, '')   // quitar <br>, <br/>, <br />
        .replace(/<[^>]*>/g, '')       // quitar todas las etiquetas HTML
        .replace(/&nbsp;/gi, '')       // quitar &nbsp;
        .replace(/\s+/g, '')           // quitar espacios, tabs, newlines
        .trim();
      if (extraTextOnly.length > 0) {
        const extraPageHtml = `
          <div style="margin-top: 40px; page-break-before: auto; padding: 20px;">
            ${extraHtml}
          </div>
        `;
        finalHtml = finalHtml + extraPageHtml;
      }

      // Wrapper HTML que replica el contenedor de LoadCafeTemplate.cshtml
      const wrappedHtml = `
<!DOCTYPE html>
<html>
<head>
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    <meta charset="utf-8" />
    <style>
        /* Estilos del proyecto C# (LoadCafeTemplate.cshtml) */
        body {
            overflow: auto;
            margin: 0;
            padding: 0;
        }

        /* Scrollbar personalizado (igual que en C#) */
        body::-webkit-scrollbar {
            width: 8px;
            height: 8px;
        }
        body::-webkit-scrollbar-thumb {
            background: #ccc;
            border-radius: 4px;
        }
        body::-webkit-scrollbar-thumb:hover {
            background: #b3b3b3;
            box-shadow: 0 0 2px 1px rgba(0, 0, 0, 0.2);
        }
        body::-webkit-scrollbar-track {
            background: #e1e1e1;
            border-radius: 4px;
        }

        /* Contenedor del template (igual que #frame-cafeTemplates-contend en C#) */
        #frame-cafeTemplates-contend {
            background-color: white;
            padding: 20px;
        }
        
        /* Justificar texto de descripcion de productos*/
        #frame-cafeTemplates-contend td {
            text-align:justify;
            }

        /* Estilos para pie de página */
        .cafe-footer {
            margin-top: 30px;
            padding-top: 20px;
            border-top: 2px solid #ccc;
            font-size: 12px;
            text-align: center;
        }
    </style>
</head>
<body>
    <div id="frame-cafeTemplates-contend">
        ${finalHtml}
        ${footer && (footer).replace(/<br\s*\/?>/gi, '').replace(/<[^>]*>/g, '').replace(/&nbsp;/gi, '').replace(/\s+/g, '').trim().length > 0 ? `<div class="cafe-footer">${footer}</div>` : ''}
    </div>
</body>
</html>
      `.trim();

      // Sanitize HTML to remove scripts (they won't work in sandboxed iframe anyway)
      const sanitizedHtml = sanitizeHtml(wrappedHtml);
      console.log('CAFE template wrapped and sanitized (replicating C# project structure), length:', sanitizedHtml.length);

      // Save rendered HTML to state
      setRenderedHtml(sanitizedHtml);
    } catch (err) {
      console.error('Error loading CAFE template:', err);
      setError(err instanceof Error ? err.message : 'Error al cargar el template CAFE');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      kicker="Plantillas · CAFE"
      title="Vista Previa del Template CAFE"
      icon={<DocumentTextIcon />}
      size="full"
      data-testid="cafe-template-modal"
    >
      <div className="space-y-4">
        {isLoading && (
          <div className="flex justify-center py-12" data-testid="cafe-template-modal-loading-spinner">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
        )}

        {error && (
          <div className="bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800 rounded-lg p-4" data-testid="cafe-template-modal-error-alert">
            <p className="text-danger-800 dark:text-danger-200 text-sm">{error}</p>
          </div>
        )}

        {!isLoading && !error && (
          <div
            className="border border-secondary-200 dark:border-secondary-700 rounded-lg overflow-auto"
            style={{
              maxHeight: '85vh',     // Limita la altura máxima
              maxWidth: '100%'       // Permite scroll horizontal
            }}
          >
            <iframe
              title="CAFE Template Preview"
              className="bg-white"
              style={{
                border: 'none',
                paddingTop: '10px',
                width: '100%',       // Igual que en _FrameCafeTamplates.cshtml del proyecto C# (width="100%")
                height: '1200px'     // Igual que en _FrameCafeTamplates.cshtml del proyecto C#
              }}
              srcDoc={renderedHtml}
              sandbox="allow-same-origin"
              data-testid="cafe-template-modal-preview-iframe"
            />
          </div>
        )}
      </div>
    </Modal>
  );
}
